import React, { useRef, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";

const shake = keyframes`
  {
    8%, 41% {
      transform: translateX(-20px);
    }
    25%, 58% {
      transform: translateX(20px);
    }
    75% {
      transform: translateX(-10px);
    }
    92% {
      transform: translateX(10px);
    }
    0%, 100% {
      transform: translateX(0);
    }
  }
`;

const Container = styled.div`
  margin: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  ${(props: IProps) => (props.invalid ? `animation: ${shake} .5s linear;` : "")}
`;

const Entry = styled.input`
  width: 37px;
  height: auto;
  margin: 0 8px;
  border: 0;
  padding: 0;
  padding-bottom: 5px;
  border-bottom: solid 3px ${({ theme }) => theme.colours.inputBorderColor};
  background-color: #ffffff;
  font-size: ${({ theme }) => theme.type.sizes.size36};
  font-weight: 700;
  color: ${({ theme }) => theme.colours.textMain};
  text-align: center;
  &:focus {
    outline: none;
  }
  :disabled {
    color: ${({ theme }) => theme.colours.buttonDisabledText}
  }
`;

interface IProps {
  size: number;
  invalid?: boolean;
  onComplete(code: string): void;
}

export const OtpEntry: React.FC<IProps> = (props) => {

  const { invalid, size, onComplete } = props;
  
  const entriesRef = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    if (invalid) {
      entriesRef.current.forEach(e => e.disabled = true);
    } else {
      entriesRef.current.forEach(e => {
        e.disabled = false;
        e.value = '';
      });
      entriesRef.current[0].focus();
    }
  }, [invalid]);

  const handleUpdateNumber = useCallback((codePart: string, index: number) => {
    const maxIndex = size - 1;
    if (codePart.length > 0) {
      if (index < maxIndex) {
        entriesRef.current[index + 1].focus();
      } else {
        entriesRef.current[maxIndex].blur();
        entriesRef.current.forEach(e => e.disabled = true);
        onComplete(entriesRef.current.map(e => e.value).join(''));
      }
    }
  }, [size, onComplete, entriesRef]);

  const onPaste = useCallback((event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text/plain").trim();
    entriesRef.current.forEach((e, i) => { 
      e.value = pasted.split("").slice(i, i + 1).join("");
      e.focus();
    });
    if (pasted.length === size) onComplete(pasted);
  }, [onComplete, entriesRef]);

  return (
    <Container { ...props }>
      {Array(size).fill(null).map((_, i) => (
        <Entry
          key={`otp-entry-${i}`}
          innerRef={el => entriesRef.current[i] = el}
          onChange={e => handleUpdateNumber(e.target.value, i)}
          onPaste={onPaste}
          autoFocus={i === 0}
          maxLength={1}
        />
      ))}
    </Container>
  );
}
